import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { NavigationStart, Router } from '@angular/router';

/**
 * Interceptor that automatically cancels previous in-flight HTTP requests
 * to the same API endpoint when a new request to that endpoint is initiated.
 *
 * This prevents race conditions and redundant network payload transfers when
 * users rapidly change filters, search keywords, tabs, or pagination.
 */
@Injectable({
  providedIn: 'root'
})
export class CancelDuplicateRequestsInterceptor implements HttpInterceptor {
  private inFlightRequests = new Map<string, Subject<void>>();

  constructor(router: Router) {
    // Cancel all in-flight GET requests on route change
    router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.cancelAllInFlightRequests();
      }
    });
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Only cancel duplicate idempotent/read (GET) requests automatically,
    // or requests explicitly flagged with X-Cancel-Duplicate header
    const isGet = req.method.toUpperCase() === 'GET';
    const hasCancelHeader = req.headers.has('X-Cancel-Duplicate');
    const hasNoCancelHeader = req.headers.has('X-No-Cancel') || req.headers.has('X-Allow-Concurrent');

    if (hasNoCancelHeader) {
      const cleaned = req.clone({
        headers: req.headers.delete('X-No-Cancel').delete('X-Allow-Concurrent')
      });
      return next.handle(cleaned);
    }

    if (!isGet && !hasCancelHeader) {
      return next.handle(req);
    }

    // Base endpoint key by stripping query params.
    // If a request includes a dataset discriminator (such as filterBy for multi-card
    // dashboards like Identity Trust Score), include it so concurrent cards do not cancel each other.
    const filterBy = this.getParamValue(req, 'filterBy');
    const filterBySuffix = (filterBy !== null && filterBy !== undefined) ? `:filterBy=${filterBy}` : '';
    const baseEndpointKey = `${req.method.toUpperCase()}:${req.url.split('?')[0]}${filterBySuffix}`;
    const offset = this.getPaginationOffset(req);

    let endpointKey: string;

    if (offset !== null && offset > 0) {
      // For scrolled pagination requests (offset > 0), key is scoped to this specific offset
      // so loading subsequent pages never cancels earlier in-flight pages.
      endpointKey = `${baseEndpointKey}:offset=${offset}`;

      // If there is already an active duplicate in-flight request for THIS EXACT offset, cancel it
      const existingSubject = this.inFlightRequests.get(endpointKey);
      if (existingSubject) {
        existingSubject.next();
        existingSubject.complete();
      }
    } else {
      // For initial page load (offset 0) or non-paginated queries (search/filter changes),
      // cancel ALL previous in-flight requests for this base endpoint (including previous pages).
      this.cancelInFlightForEndpoint(baseEndpointKey);
      endpointKey = baseEndpointKey;
    }

    // Create a new cancellation Subject for the new request
    const cancelSubject = new Subject<void>();
    this.inFlightRequests.set(endpointKey, cancelSubject);

    // Clean up request headers if internal header was present
    const cleanedReq = hasCancelHeader
      ? req.clone({ headers: req.headers.delete('X-Cancel-Duplicate') })
      : req;

    return next.handle(cleanedReq).pipe(
      takeUntil(cancelSubject),
      finalize(() => {
        // Only clean up the map entry if it matches this specific request's Subject
        if (this.inFlightRequests.get(endpointKey) === cancelSubject) {
          this.inFlightRequests.delete(endpointKey);
        }
      })
    );
  }

  private getParamValue(req: HttpRequest<unknown>, name: string): string | null {
    if (req.params.has(name)) {
      return req.params.get(name);
    }
    const questionIdx = req.url.indexOf('?');
    if (questionIdx !== -1) {
      const queryStr = req.url.slice(questionIdx + 1);
      const params = new URLSearchParams(queryStr);
      if (params.has(name)) {
        return params.get(name);
      }
    }
    return null;
  }

  private getPaginationOffset(req: HttpRequest<unknown>): number | null {
    if (req.params.has('offset')) {
      const val = Number(req.params.get('offset'));
      return isNaN(val) ? null : val;
    }
    if (req.params.has('pageIndex')) {
      const val = Number(req.params.get('pageIndex'));
      return isNaN(val) ? null : val;
    }
    if (req.params.has('pageNumber')) {
      const val = Number(req.params.get('pageNumber'));
      return isNaN(val) ? null : val;
    }

    const questionIdx = req.url.indexOf('?');
    if (questionIdx !== -1) {
      const queryStr = req.url.slice(questionIdx + 1);
      const params = new URLSearchParams(queryStr);
      for (const paramName of ['offset', 'pageIndex', 'pageNumber']) {
        if (params.has(paramName)) {
          const val = Number(params.get(paramName));
          if (!isNaN(val)) {
            return val;
          }
        }
      }
    }

    return null;
  }

  private cancelInFlightForEndpoint(baseEndpointKey: string): void {
    const prefix = `${baseEndpointKey}:offset=`;
    for (const [key, subject] of this.inFlightRequests.entries()) {
      if (key === baseEndpointKey || key.startsWith(prefix)) {
        subject.next();
        subject.complete();
        this.inFlightRequests.delete(key);
      }
    }
  }

  public cancelAllInFlightRequests(): void {
    this.inFlightRequests.forEach(subject => {
      subject.next();
      subject.complete();
    });
    this.inFlightRequests.clear();
  }
}
